var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../services/api-client';
let JvAdminParents = class JvAdminParents extends LitElement {
    constructor() {
        super(...arguments);
        this._parents = [];
        this._assignments = [];
        this._cameras = [];
        this._loading = true;
        this._tab = 'parents';
        this._showInvite = false;
        this._inviteEmail = '';
        this._inviteName = '';
        this._inviteError = '';
        this._inviting = false;
        this._showAssign = false;
        this._assignUserId = '';
        this._assignCameraId = '';
        this._assignError = '';
        this._assigning = false;
        this._loadError = '';
    }
    static { this.styles = css `
    :host { display: block; }
    .tab-btn { padding: 8px 20px; font-size: 13px; font-weight: 600; border: none; background: none; cursor: pointer; color: var(--jv-ink-2); border-bottom: 2px solid transparent; }
    .tab-btn.active { color: var(--jv-primary); border-bottom-color: var(--jv-primary); }
  `; }
    connectedCallback() {
        super.connectedCallback();
        void this._load();
    }
    async _load() {
        this._loading = true;
        this._loadError = '';
        try {
            const [parents, assignments, cameras] = await Promise.all([
                api.get('/api/parents'),
                api.get('/api/assignments'),
                api.get('/api/admin/cameras'),
            ]);
            this._parents = parents;
            this._assignments = assignments;
            this._cameras = cameras;
        }
        catch (err) {
            this._loadError = err?.message ?? 'Unable to load parents and assignments. Try again.';
        }
        finally {
            this._loading = false;
        }
    }
    async _handleInvite(e) {
        e.preventDefault();
        if (!this._inviteEmail || !this._inviteName) {
            this._inviteError = 'Email and name are required.';
            return;
        }
        this._inviting = true;
        this._inviteError = '';
        try {
            await api.post('/api/parents/invite', { email: this._inviteEmail, displayName: this._inviteName });
            this._showInvite = false;
            this._inviteEmail = '';
            this._inviteName = '';
            await this._load();
        }
        catch (err) {
            this._inviteError = err?.message ?? 'Failed to invite parent.';
        }
        finally {
            this._inviting = false;
        }
    }
    async _handleAssign(e) {
        e.preventDefault();
        if (!this._assignUserId || !this._assignCameraId) {
            this._assignError = 'Select a parent and camera.';
            return;
        }
        this._assigning = true;
        this._assignError = '';
        try {
            await api.post('/api/assignments', { userId: this._assignUserId, cameraId: this._assignCameraId });
            this._showAssign = false;
            this._assignUserId = '';
            this._assignCameraId = '';
            await this._load();
        }
        catch (err) {
            this._assignError = err?.message ?? 'Failed to assign camera.';
        }
        finally {
            this._assigning = false;
        }
    }
    async _removeAssignment(assignment) {
        if (!confirm(`Remove ${assignment.cameraName} from ${assignment.userName}?`))
            return;
        try {
            await api.delete(`/api/assignments/${assignment.id}`);
            this._assignments = this._assignments.filter(a => a.id !== assignment.id);
        }
        catch (err) {
            this._assignError = err?.message ?? 'Failed to remove assignment.';
        }
    }
    _back() {
        this.dispatchEvent(new CustomEvent('admin-back', { bubbles: true, composed: true }));
    }
    render() {
        return html `
      <div class="max-w-content mx-auto px-5 sm:px-8 py-7">
        <div class="flex items-center gap-4 mb-6">
          <button @click=${this._back} class="text-sm text-jv-primary font-semibold hover:underline">&larr; Dashboard</button>
          <h1 class="text-2xl font-extrabold text-jv-ink">Parents & Access</h1>
          <div class="ml-auto flex gap-2">
            <button @click=${() => this._showAssign = !this._showAssign}
              class="jv-btn-primary text-xs px-4 py-2">+ Assign Camera</button>
            <button @click=${() => this._showInvite = !this._showInvite}
              class="jv-btn-primary text-xs px-4 py-2">+ Invite Parent</button>
          </div>
        </div>

        <div class="flex border-b border-jv-border mb-5">
          <button class="tab-btn ${this._tab === 'parents' ? 'active' : ''}" @click=${() => this._tab = 'parents'}>Parents</button>
          <button class="tab-btn ${this._tab === 'assignments' ? 'active' : ''}" @click=${() => this._tab = 'assignments'}>Feed Assignments</button>
        </div>

        ${this._loadError ? html `<div class="jv-card p-6 mb-5" role="alert"><p class="text-jv-danger text-sm">${this._loadError}</p><button @click=${this._load} class="jv-btn-primary text-xs mt-4">Retry</button></div>` : ''}

        ${this._showInvite ? html `
          <div class="jv-card mb-5"><div class="p-5">
            <h2 class="font-bold text-jv-ink mb-4">Invite Parent</h2>
            ${this._inviteError ? html `<p class="text-jv-danger text-sm mb-4">${this._inviteError}</p>` : ''}
            <form @submit=${this._handleInvite}>
              <div class="grid sm:grid-cols-2 gap-4 mb-4">
                <div><label class="jv-label">Name</label><input class="jv-input" .value=${this._inviteName} @input=${(e) => this._inviteName = e.target.value} placeholder="Ana Silva" /></div>
                <div><label class="jv-label">Email</label><input class="jv-input" type="email" .value=${this._inviteEmail} @input=${(e) => this._inviteEmail = e.target.value} placeholder="ana@example.com" /></div>
              </div>
              <button type="submit" ?disabled=${this._inviting} class="jv-btn-primary">${this._inviting ? 'Inviting…' : 'Send Invite'}</button>
            </form>
          </div></div>
        ` : ''}

        ${this._showAssign ? html `
          <div class="jv-card mb-5"><div class="p-5">
            <h2 class="font-bold text-jv-ink mb-4">Assign Camera to Parent</h2>
            ${this._assignError ? html `<p class="text-jv-danger text-sm mb-4">${this._assignError}</p>` : ''}
            <form @submit=${this._handleAssign}>
              <div class="grid sm:grid-cols-2 gap-4 mb-4">
                <div><label class="jv-label">Parent</label>
                  <select class="jv-input" @change=${(e) => this._assignUserId = e.target.value}>
                    <option value="">Select parent…</option>
                    ${this._parents.map(p => html `<option value=${p.id}>${p.displayName} (${p.email})</option>`)}
                  </select>
                </div>
                <div><label class="jv-label">Camera</label>
                  <select class="jv-input" @change=${(e) => this._assignCameraId = e.target.value}>
                    <option value="">Select camera…</option>
                    ${this._cameras.map(c => html `<option value=${c.id}>${c.name} (${c.siteName})</option>`)}
                  </select>
                </div>
              </div>
              <button type="submit" ?disabled=${this._assigning} class="jv-btn-primary">${this._assigning ? 'Assigning…' : 'Assign'}</button>
            </form>
          </div></div>
        ` : ''}

        ${this._loading ? html `<p class="text-jv-ink-2 text-sm">Loading…</p>` : html `
          ${this._tab === 'parents' ? html `
            ${this._parents.length === 0 ? html `
              <div class="jv-card"><div class="p-10 text-center"><p class="font-semibold text-jv-ink">No parents yet</p><p class="text-sm text-jv-ink-2 mt-1">Invite parents to give them camera access.</p></div></div>
            ` : html `
              <div class="jv-card">
                <table class="w-full text-sm">
                  <thead><tr class="border-b border-jv-border">
                    <th class="text-left p-3 text-xs uppercase text-jv-ink-2 font-semibold">Name</th>
                    <th class="text-left p-3 text-xs uppercase text-jv-ink-2 font-semibold">Email</th>
                    <th class="text-center p-3 text-xs uppercase text-jv-ink-2 font-semibold">Cameras</th>
                    <th class="text-center p-3 text-xs uppercase text-jv-ink-2 font-semibold">Status</th>
                  </tr></thead>
                  <tbody>
                    ${this._parents.map(p => html `
                      <tr class="border-b border-jv-border last:border-0 hover:bg-jv-surface-2">
                        <td class="p-3 font-semibold text-jv-ink">${p.displayName}</td>
                        <td class="p-3 text-jv-ink-2">${p.email}</td>
                        <td class="p-3 text-center text-jv-ink">${p.assignedCameraCount}</td>
                        <td class="p-3 text-center"><span class="inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${p.isActive ? 'bg-jv-success-050 text-jv-success' : 'bg-jv-surface-2 text-jv-ink-2'}">${p.isActive ? 'Active' : 'Inactive'}</span></td>
                      </tr>
                    `)}
                  </tbody>
                </table>
              </div>
            `}
          ` : html `
            ${this._assignments.length === 0 ? html `
              <div class="jv-card"><div class="p-10 text-center"><p class="font-semibold text-jv-ink">No assignments yet</p><p class="text-sm text-jv-ink-2 mt-1">Assign cameras to parents so they can view them.</p></div></div>
            ` : html `
              <div class="jv-card">
                <table class="w-full text-sm">
                  <thead><tr class="border-b border-jv-border">
                    <th class="text-left p-3 text-xs uppercase text-jv-ink-2 font-semibold">Parent</th>
                    <th class="text-left p-3 text-xs uppercase text-jv-ink-2 font-semibold">Camera</th>
                    <th class="text-right p-3 text-xs uppercase text-jv-ink-2 font-semibold"></th>
                  </tr></thead>
                  <tbody>
                    ${this._assignments.map(a => html `
                      <tr class="border-b border-jv-border last:border-0 hover:bg-jv-surface-2">
                        <td class="p-3"><span class="font-semibold text-jv-ink">${a.userName}</span><span class="text-jv-ink-2 ml-2 text-xs">${a.userEmail}</span></td>
                        <td class="p-3 text-jv-ink">${a.cameraName}</td>
                        <td class="p-3 text-right"><button @click=${() => this._removeAssignment(a)} class="text-xs text-jv-danger font-semibold hover:underline">Remove</button></td>
                      </tr>
                    `)}
                  </tbody>
                </table>
              </div>
            `}
          `}
        `}
      </div>
    `;
    }
};
__decorate([
    state()
], JvAdminParents.prototype, "_parents", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_assignments", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_cameras", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_loading", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_tab", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_showInvite", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_inviteEmail", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_inviteName", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_inviteError", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_inviting", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_showAssign", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_assignUserId", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_assignCameraId", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_assignError", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_assigning", void 0);
__decorate([
    state()
], JvAdminParents.prototype, "_loadError", void 0);
JvAdminParents = __decorate([
    customElement('jv-admin-parents')
], JvAdminParents);
export { JvAdminParents };
